import React, { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createZitadelAuth, ZitadelConfig } from '@zitadel/react';
import { User } from 'oidc-client-ts';

const authority = (import.meta as any).env.VITE_OIDC_AUTHORITY;
const client_id = (import.meta as any).env.VITE_OIDC_CLIENT_ID;
const organization_id = (import.meta as any).env.VITE_OIDC_ORGANIZATION_ID;
const redirect_uri = (import.meta as any).env.VITE_OIDC_REDIRECT_URI;
const post_logout_redirect_uri = (import.meta as any).env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI;
const project_id = (import.meta as any).env.VITE_OIDC_PROJECT_ID;
const rawApiBaseUrl = ((import.meta as any).env.VITE_API_BASE_URL as string | undefined) || '/api/v1';
const apiBaseUrl = rawApiBaseUrl.replace(/\/$/, '');
const meEndpoint = `${apiBaseUrl}/me`;
const isDev = Boolean((import.meta as any).env?.DEV);

// Create Zitadel auth instance
const config = {
  authority: authority,
  client_id: client_id,
  redirect_uri: `${redirect_uri}/callback`,
  post_logout_redirect_uri: post_logout_redirect_uri,
  scope: [
    "openid",
    "profile",
    "email",
    "offline_access",
    `urn:zitadel:iam:org:project:id:${project_id}:aud`,
    "urn:zitadel:iam:org:projects:roles",
    "urn:zitadel:iam:user:metadata",
    "urn:zitadel:iam:org:project:id:zitadel:aud",
    `urn:zitadel:iam:org:id:${organization_id}`
  ].join(" "), // Added offline_access for refresh tokens
  // Enable automatic silent renewal using refresh tokens
  automaticSilentRenew: true,
  // Optional: configure token renewal timing
  accessTokenExpiringNotificationTimeInSeconds: 60, // Start renewal 60 seconds before expiry
  silentRequestTimeout: 10000, // 10 second timeout for silent renewal
  // Enable refresh token rotation (recommended for security)
  includeIdTokenInSilentRenew: true,
} as ZitadelConfig;

const zitadel = createZitadelAuth(config);

const hasUsableAccessToken = (candidate: User | null | undefined): candidate is User =>
  Boolean(candidate?.access_token);

export interface CurrentUserProfile {
  sub: string;
  email: string;
  org_id: string | null;
  roles: string[];
  organization_setup_required: boolean;
  organization_subscription_required?: boolean;
  permissions: string[];
  referral_partner_name?: string | null;
}

// Auth context
interface AuthContextType {
  user: User | null;
  authenticated: boolean | null;
  login: () => void;
  logout: () => void;
  isLoading: boolean;
  handleCallback: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  isTokenExpiring: boolean;
  currentUser: CurrentUserProfile | null;
  isCurrentUserLoading: boolean;
  reloadCurrentUser: () => Promise<CurrentUserProfile | null>;
  permissions: string[];
  organizationSetupRequired: boolean;
  organizationSubscriptionRequired: boolean;
  impersonatedOrgId: string | null;
  setImpersonatedOrgId: (orgId: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTokenExpiring, setIsTokenExpiring] = useState(false);
  const [callbackProcessed, setCallbackProcessed] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUserProfile | null>(null);
  const [isCurrentUserLoading, setIsCurrentUserLoading] = useState(false);
  const [impersonatedOrgId, setImpersonatedOrgIdState] = useState<string | null>(null);
  const currentUserAbortController = useRef<AbortController | null>(null);
  const lastUserSubRef = useRef<string | null>(null);

  if (!authority || !client_id) {
    console.error('Missing Zitadel configuration:', { authority, client_id });
    return (
      <div>
        Zitadel configuration error. Please check your environment variables.
      </div>
    );
  }

  const resetCurrentUserState = useCallback(() => {
    currentUserAbortController.current?.abort();
    currentUserAbortController.current = null;
    setCurrentUser(null);
    setIsCurrentUserLoading(false);
    setImpersonatedOrgIdState(null);
    lastUserSubRef.current = null;
  }, []);

  const fetchCurrentUser = useCallback(async (tokenOverride?: string): Promise<CurrentUserProfile | null> => {
    const accessToken = tokenOverride ?? user?.access_token;

    if (!accessToken) {
      resetCurrentUserState();
      return null;
    }

    // Abort any pending request before starting a new one
    currentUserAbortController.current?.abort();
    const controller = new AbortController();
    currentUserAbortController.current = controller;

    setIsCurrentUserLoading(true);

    try {
      const response = await fetch(meEndpoint, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json'
        },
        signal: controller.signal
      });

      if (response.status === 401) {
        resetCurrentUserState();
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to load current user profile (${response.status})`);
      }

      const data: CurrentUserProfile = await response.json();
      setCurrentUser(data);
      return data;
    } catch (error) {
      if (controller.signal.aborted) {
        return null;
      }

      if (isDev) {
        console.error('Failed to load current user profile:', error);
      }

      resetCurrentUserState();
      return null;
    } finally {
      if (!controller.signal.aborted) {
        setIsCurrentUserLoading(false);
        currentUserAbortController.current = null;
      }
    }
  }, [user?.access_token, resetCurrentUserState]);

  const reloadCurrentUser = useCallback(() => fetchCurrentUser(), [fetchCurrentUser]);

  const login = () => {
    zitadel.authorize();
  };

  const logout = () => {
    zitadel.signout();
    setUser(null);
    setAuthenticated(false);
    setCallbackProcessed(false);
    resetCurrentUserState();
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const renewedUser = await zitadel.userManager.signinSilent();
      if (renewedUser) {
        setUser(renewedUser);
        setAuthenticated(true);
        await fetchCurrentUser(renewedUser.access_token);
        return true;
      }
      return false;
    } catch (error) {
      // Only log in development
      if (isDev) {
        console.error('Manual token refresh failed:', error);
      }
      return false;
    }
  };

  const handleCallback = async () => {
    // Prevent multiple callback processing
    if (callbackProcessed) {
      return;
    }

    try {
      setIsLoading(true);
      setCallbackProcessed(true);
      
      // Clear any existing URL parameters to prevent reprocessing
      const url = new URL(window.location.href);
      const hasCallbackParams = url.searchParams.has('code') || url.searchParams.has('state');
      
      if (!hasCallbackParams) {
        // No callback parameters, likely already processed
        setUser(null);
        setAuthenticated(false);
        resetCurrentUserState();
        return;
      }

      const user = await zitadel.userManager.signinRedirectCallback();
      if (hasUsableAccessToken(user)) {
        setUser(user);
        setAuthenticated(true);
        
        // Clean up the URL after successful callback
        window.history.replaceState({}, document.title, '/dashboard');
      } else {
        await zitadel.userManager.removeUser();
        setUser(null);
        setAuthenticated(false);
        resetCurrentUserState();
      }
    } catch (error) {
      if (isDev) {
        console.error('Callback error:', error);
      }
      setUser(null);
      setAuthenticated(false);
      resetCurrentUserState();
      
      // If callback fails, redirect to login
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        // Check if we're on the callback URL and haven't processed it yet
        if (window.location.pathname === '/callback' && !callbackProcessed) {
          await handleCallback();
          return;
        }

        // Skip initialization if we're still on callback page after processing
        if (window.location.pathname === '/callback') {
          return;
        }

        // Otherwise, check for existing user
        const user = await zitadel.userManager.getUser();
        if (hasUsableAccessToken(user) && !user.expired) {
          setUser(user);
          setAuthenticated(true);
        } else {
          await zitadel.userManager.removeUser();
          setUser(null);
          setAuthenticated(false);
          resetCurrentUserState();
        }
      } catch (error) {
        if (isDev) {
          console.error('Auth initialization error:', error);
        }
        setUser(null);
        setAuthenticated(false);
        resetCurrentUserState();
      } finally {
        setIsLoading(false);
      }
    };

    // Set up event handlers for token management
    const handleUserLoaded = (user: User) => {
      if (isDev) {
        console.log('User loaded/renewed:', user.profile?.preferred_username, {
          hasAccessToken: Boolean(user.access_token),
          expired: user.expired,
        });
      }
      if (!hasUsableAccessToken(user)) {
        setUser(null);
        setAuthenticated(false);
        resetCurrentUserState();
        return;
      }
      setUser(user);
      setAuthenticated(true);
      setIsTokenExpiring(false);
    };

    const handleUserUnloaded = () => {
      if (isDev) {
        console.log('User session ended');
      }
      setUser(null);
      setAuthenticated(false);
      resetCurrentUserState();
    };

    const handleAccessTokenExpiring = () => {
      if (isDev) {
        console.log('Access token expiring, attempting renewal...');
      }
      setIsTokenExpiring(true);
    };

    const handleAccessTokenExpired = () => {
      if (isDev) {
        console.log('Access token expired');
      }
      setIsTokenExpiring(false);
      
      // If both access and refresh tokens are expired, log out the user
      setTimeout(async () => {
        try {
          const currentUser = await zitadel.userManager.getUser();
          if (!currentUser || currentUser.expired) {
            if (isDev) {
              console.log('Both tokens expired, logging out user');
            }
            logout();
          }
        } catch {
          // If we can't get user info, assume tokens are invalid
          logout();
        }
      }, 1000); // Small delay to allow for any ongoing renewal attempts
    };

    const handleSilentRenewError = (error: Error) => {
      if (isDev) {
        console.error('Silent token renewal failed:', error);
      }
      setIsTokenExpiring(false);
      
      // Handle refresh token expiration or invalid grants
      const isRefreshTokenInvalid = error.message.includes('invalid_grant') || 
                                   error.message.includes('refresh_token') ||
                                   error.message.includes('expired');
      
      if (isRefreshTokenInvalid) {
        if (isDev) {
          console.log('Refresh token invalid or expired, logging out user');
        }
        logout();
      }
    };

    // Register event listeners
    zitadel.userManager.events.addUserLoaded(handleUserLoaded);
    zitadel.userManager.events.addUserUnloaded(handleUserUnloaded);
    zitadel.userManager.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    zitadel.userManager.events.addAccessTokenExpired(handleAccessTokenExpired);
    zitadel.userManager.events.addSilentRenewError(handleSilentRenewError);

    initAuth();

    // Cleanup event listeners on unmount
    return () => {
      zitadel.userManager.events.removeUserLoaded(handleUserLoaded);
      zitadel.userManager.events.removeUserUnloaded(handleUserUnloaded);
      zitadel.userManager.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      zitadel.userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      zitadel.userManager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, [callbackProcessed, fetchCurrentUser, resetCurrentUserState]); // Add dependency to prevent infinite loops

  useEffect(() => {
    if (!user || user.expired) {
      resetCurrentUserState();
      return;
    }

    fetchCurrentUser(user.access_token);
  }, [user, fetchCurrentUser, resetCurrentUserState]);

  useEffect(() => {
    if (!currentUser) {
      lastUserSubRef.current = null;
      setImpersonatedOrgIdState(null);
      return;
    }

    if (lastUserSubRef.current !== currentUser.sub) {
      lastUserSubRef.current = currentUser.sub;
      setImpersonatedOrgIdState(currentUser.org_id);
      return;
    }

    if (impersonatedOrgId === null && currentUser.org_id) {
      setImpersonatedOrgIdState(currentUser.org_id);
    }
  }, [currentUser, impersonatedOrgId]);

  const updateImpersonatedOrgId = useCallback((orgId: string | null) => {
    setImpersonatedOrgIdState(orgId);
  }, []);

  const contextValue = useMemo(() => ({
    user,
    authenticated,
    login,
    logout,
    isLoading,
    handleCallback,
    refreshToken,
    isTokenExpiring,
    currentUser,
    isCurrentUserLoading,
    reloadCurrentUser,
    permissions: currentUser?.permissions ?? [],
    organizationSetupRequired: currentUser?.organization_setup_required ?? false,
    organizationSubscriptionRequired: currentUser?.organization_subscription_required ?? false,
    impersonatedOrgId,
    setImpersonatedOrgId: updateImpersonatedOrgId
  }), [
    user,
    authenticated,
    isLoading,
    handleCallback,
    refreshToken,
    isTokenExpiring,
    currentUser,
    isCurrentUserLoading,
    reloadCurrentUser,
    currentUser?.organization_subscription_required,
    impersonatedOrgId,
    updateImpersonatedOrgId
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useAuthToken = (): string | undefined => {
  const { user } = useAuth();
  return user?.access_token;
};

export const useCurrentUser = () => {
  const { currentUser, isCurrentUserLoading, reloadCurrentUser, permissions, organizationSetupRequired, organizationSubscriptionRequired, impersonatedOrgId, setImpersonatedOrgId } = useAuth();
  return {
    currentUser,
    isCurrentUserLoading,
    reloadCurrentUser,
    permissions,
    organizationSetupRequired,
    organizationSubscriptionRequired,
    impersonatedOrgId,
    setImpersonatedOrgId
  };
};

